import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    inject
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { LetDirective } from '@ngrx/component';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { TuiButtonModule } from '@taiga-ui/core';
import { BOOKING_TIME_MINUTE_STEP } from '@us/web/cart/util';
import { getHours, getMinutes, isBefore } from 'date-fns';
import { FormatPipeModule, ParseIsoPipeModule } from 'ngx-date-fns';
import { BehaviorSubject, combineLatest, map, take, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'us-select-time-v2',
    standalone: true,
    imports: [
        TranslocoModule,
        ScrollingModule,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        AsyncPipe,
        TuiButtonModule,
        ParseIsoPipeModule,
        FormatPipeModule,
        LetDirective
    ],
    providers: [TuiDestroyService],
    templateUrl: './select-time-v2.component.html',
    styleUrls: ['./select-time-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectTimeV2Component implements OnInit {
    @ViewChild('hoursViewport') hoursViewport: CdkVirtualScrollViewport | undefined;
    @ViewChild('minutesViewport') minutesViewport: CdkVirtualScrollViewport | undefined;

    private readonly destroy$ = inject(TuiDestroyService, { self: true });

    readonly MINUTES = Array.from({ length: 60 / BOOKING_TIME_MINUTE_STEP }, (_, i) => i * BOOKING_TIME_MINUTE_STEP);

    readonly min$ = new BehaviorSubject<[number, number]>([0, 0]);
    readonly hours$ = new BehaviorSubject<number[]>([]);
    readonly minutes$ = new BehaviorSubject<number[]>(this.MINUTES);

    readonly selectedHourIndex$ = new BehaviorSubject<number>(0);
    readonly selectedMinuteIndex$ = new BehaviorSubject<number>(0);

    readonly selectedTime$ = combineLatest([
        this.selectedHourIndex$,
        this.selectedMinuteIndex$,
        this.hours$,
        this.minutes$
    ]).pipe(
        map(([hourIndex, minuteIndex, hours, minutes]) => {
            const hour = hours[hourIndex];
            const minute = minutes[minuteIndex];
            return new Date(new Date().setHours(hour, minute, 0, 0));
        })
    );

    readonly isValid$ = combineLatest([this.selectedTime$, this.min$]).pipe(
        map(([time, [hour, minute]]) => {
            const min = new Date(new Date().setHours(hour, minute, 0, 0));
            return !isBefore(time, min);
        })
    );

    @Input() set min(min: Date) {
        let minHour = min ? getHours(min) : 0;
        let minMinute = min ? getMinutes(min) : 0;
        if (minMinute > 60 - BOOKING_TIME_MINUTE_STEP) {
            minHour += 1;
            minMinute = 0;
        }

        this.hours$.next(Array.from({ length: 23 - minHour + 1 }, (_, i) => i + minHour));
        this.min$.next([minHour, minMinute]);
    }

    @Input() set value(value: Date | null) {
        if (!value) {
            return;
        }
        const hour = getHours(value);
        const minute = getMinutes(value);
        const hourIndex = this.hours$.value.findIndex(h => h === hour);
        const minuteIndex = this.minutes$.value.findIndex(m => m === minute);
        this.selectedHourIndex$.next(hourIndex);
        this.selectedMinuteIndex$.next(minuteIndex);
    }

    @Output() valueChange = new EventEmitter<Date>();

    ngOnInit(): void {
        this.selectedHourIndex$
            .asObservable()
            .pipe(
                takeUntil(this.destroy$),
                map(index => this.hours$.value[index]),
                tap(hour => {
                    const [minHour, minMinute] = this.min$.value;
                    if (hour === minHour) {
                        this.minutes$.next(this.MINUTES.filter(minute => minute >= minMinute));
                    } else {
                        this.minutes$.next(this.MINUTES);
                    }
                })
            )
            .subscribe();
    }

    handleSubmit() {
        this.selectedTime$.pipe(take(1)).subscribe(time => {
            this.valueChange.emit(time);
        });
    }

    changeHour(index: number) {
        this.selectedHourIndex$.next(index);
    }

    changeMinute(index: number) {
        this.selectedMinuteIndex$.next(index);
    }
}
